import { useFetchKey, useRuntimeConfigKey, type UseFetch, type UseRuntimeConfig } from '@/injectionKeys';
import { WabunkaAPIError } from '@/models/error';
import { buildFetchKey } from '@/wabunka-src/common/utils/buildFetchKey';

import type { WabunkaError } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_error';
import type { AsyncDataOptions } from 'nuxt/app';

export interface UseFetchFromPublicWabunkaApiArgs<T> {
  path: Ref<string>; // データ取得先（例：'/experiences'）
  query?: Ref<object | undefined>; // API リクエスト用のクエリオブジェクト
  /**
   * HTTP メソッド。既定は GET。
   *
   * 読み取りでも POST を採ることがある。識別子をパス・クエリに載せるとアクセスログや監視サービスの
   * リクエスト URL に残るため、秘匿すべき値は body で送る。
   */
  method?: 'GET' | 'POST';
  body?: Ref<object | undefined>; // POST 時のリクエストボディ
  /**
   * useFetch のオプションを指定したいときに使用
   * customKeyの使用場面
   * - 同じcomposableから使用するが異なるキャッシュを持ちたい時
   * - コンポーネントごとに独立したデータ取得が必要な時
   * */
  options?: AsyncDataOptions<T> & { customKey?: string };
  converter: (res: unknown) => T; // json レスポンスに型を付けて返却するコンバーター（基本的に ts-proto が自動生成する fromJson 関数を渡せば OK ）
  fetchKey?: string; // フェッチのキーを指定したい場合に利用する（取得したデータがリクエスト間で重複排除されず、データを個別に扱いたいときに使用する）
  throwOnError?: boolean; // useAsyncFetchFromPublicWabunkaApi でのみ有効。エラー時に throw するかどうか（デフォルト: true）。false の場合はエラーでも throw せず { data, status, refresh } を返す
}

/**
 * ヘッダーの組み立てをおこなう
 * @returns ヘッダー用オブジェクト
 */
const buildHeader = () => {
  return {
    // eslint-disable-next-line
    'Content-Type': 'application/json',
    Accept: 'application/json',
    // eslint-disable-next-line
    'X-Wabunka-Language': 'en',
  };
};

/**
 * Public Wabunka API からデータを取得する useFetch ラッパー Composable
 * ページ層では直接使用せず、ドメイン知識を有した Composable を介して利用してください
 *
 * @param UseFetchFromPublicWabunkaApiArgs
 * @return UseFetch と同じ返り値の型
 */
export const useFetchFromPublicWabunkaApi = <T>({ path, query, method, body, options, converter }: UseFetchFromPublicWabunkaApiArgs<T>) => {
  const useRuntimeConfig = inject(useRuntimeConfigKey) as UseRuntimeConfig;
  const runtimeConfig = useRuntimeConfig();
  const useFetch = inject(useFetchKey) as UseFetch;

  // Nuxt 4 では useFetch/useAsyncData が同じ key のときに data/error/status を共有するため、
  // リクエストを区別する入力をすべて key に含める。とくに body を落とすと、同じ path への
  // 別内容の POST が 1 回目の応答をそのまま返す。
  const { customKey, ...restOptions } = options ?? {};
  const key = buildFetchKey(path.value, query?.value, customKey, { method, body: body?.value });

  const { data, pending, error, execute, refresh, status } = useFetch(path, {
    key,
    ...restOptions,
    query,
    ...(method !== undefined ? { method } : {}),
    ...(body !== undefined ? { body } : {}),
    baseURL: runtimeConfig.public.WABUNKA_PUBLIC_API_BASE_URL as string,
    onRequest({ options }) {
      options.headers = new Headers(buildHeader());
    },
  });

  const convertedError = computed(() => {
    if (error.value == null) {
      return null;
    }
    const data = error.value.data as WabunkaError;
    return new WabunkaAPIError(data);
  });

  const convertedData = computed<T | undefined>(() => {
    if (!data.value) return;
    return converter(data.value);
  });

  return { data: convertedData, pending, error: convertedError, execute, refresh, status };
};
